import type { Role, RateMultiplier } from '@/types';
import { getSampleRole, getThreshold, getPrevailingRateMultiplier, getMultiplierListStringHorizontal, getSortedOvertimeMultipliers, calculatePay, type RateCalculationContext } from '@/utils/rateCalculations';

const parseTime = (time: string | null): number | null => {
  if (!time) return null;
  const parts = time.split(':').map(Number);
  if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
  return parts[0] * 60 + parts[1];
};

function generateOvernightExamplePayCalculationRows(
  workHours: number,
  context: RateCalculationContext,
  premiumOptions: {
    premiumType: 'offset_overtime' | 'guaranteed_rate' | 'hourly_premium' | 'flat_rate' | 'credit_extra_time' | null;
    startTime: string;
    endTime: string;
    exampleShiftStartHour: number;
    // For hourly_premium
    premiumKind?: 'percentage' | 'fixed' | null;
    premiumPercentage: number;
    premiumAmount: number;
    isOtInclusive?: boolean;
    // For credit_extra_time
    creditMinutes: number;
  }
) {
  const { roles, rateMultipliers, minimumBillableHours } = context;
  const sampleRole = getSampleRole(roles);
  const billingStructure = sampleRole.billingStructure;

  const {
    premiumType, startTime, endTime, exampleShiftStartHour, premiumKind, premiumPercentage, premiumAmount, isOtInclusive, creditMinutes
  } = premiumOptions;

  const straightTimeMultiplier = rateMultipliers.find(m => m.code === 'STRAIGHT_TIME')!;
  const ladder = getSortedOvertimeMultipliers(rateMultipliers, billingStructure);
  const allMultipliers = [straightTimeMultiplier, ...ladder];
  
  const isDailyLadder = ladder.length > 0 && ladder[0].defaultPerShiftThresholdHours != null;
  const ladderBillingStructure = isDailyLadder ? 'day_rate' : 'hourly';

  const dayRateGuaranteedHours = billingStructure === 'day_rate' ? (minimumBillableHours ?? 8) : 0;
  const baseHourlyRate = billingStructure === 'day_rate' && sampleRole.rate && dayRateGuaranteedHours > 0
    ? sampleRole.rate / dayRateGuaranteedHours
    : (sampleRole.rate || 50);

  const rows: Array<Record<string, string | number | boolean>> = [];
  let total_normal_pay = 0;
  let total_premium_pay = 0;

  const overnightStartMinutes = parseTime(startTime);
  const overnightEndMinutes = parseTime(endTime);

  const splitPoints = new Set<number>([0, workHours]);
  allMultipliers.forEach(m => {
    const threshold = getThreshold(m, ladderBillingStructure);
    if (threshold > 0 && threshold < workHours) {
      splitPoints.add(threshold);
    }
  });

  if (overnightStartMinutes !== null && overnightEndMinutes !== null) {
    const shiftStartMinutes = exampleShiftStartHour * 60;
    
    let startDiff = overnightStartMinutes - shiftStartMinutes;
    if (startDiff < 0) startDiff += 24 * 60;
    const overnightStartHoursIntoShift = startDiff / 60;

    let endDiff = overnightEndMinutes - shiftStartMinutes;
    if (endDiff < startDiff) endDiff += 24 * 60;
    const overnightEndHoursIntoShift = endDiff / 60;

    if (overnightStartHoursIntoShift > 0 && overnightStartHoursIntoShift < workHours) {
      splitPoints.add(overnightStartHoursIntoShift);
    }
    if (overnightEndHoursIntoShift > 0 && overnightEndHoursIntoShift < workHours) {
      splitPoints.add(overnightEndHoursIntoShift);
    }
  }

  const sortedSplitPoints = Array.from(splitPoints).sort((a,b) => a-b).filter(p => p < workHours);

  for (let i = 0; i < sortedSplitPoints.length; i++) {
    const segmentStart = sortedSplitPoints[i];
    const segmentEnd = sortedSplitPoints[i+1] || workHours;
    const hoursInSegment = segmentEnd - segmentStart;
    if (hoursInSegment <= 1e-9) continue;
    
    const midpoint = segmentStart + hoursInSegment / 2;
    const totalHoursWorked = midpoint + ((context as RateCalculationContext & { startingHoursInWeek?: number }).startingHoursInWeek || 0);
    const prevailingMultiplier = getPrevailingRateMultiplier(totalHoursWorked, rateMultipliers, ladderBillingStructure === 'hourly' ? 'hourly' : 'day_rate');
    const normalPay = hoursInSegment * baseHourlyRate * prevailingMultiplier.defaultMultiplier!;
    total_normal_pay += normalPay;

    let premium = 0;
    let effective_rate = `${prevailingMultiplier.name} (${prevailingMultiplier.defaultMultiplier!}x)`;
    let hourly_premium_rate = '—';
    let hourly_premium_rate_simple = '—';
    
    const midpointTimeOfDay = ((exampleShiftStartHour * 60) + (midpoint * 60)) % (24 * 60);
    let isOvernight = false;
    if (overnightStartMinutes !== null && overnightEndMinutes !== null) {
      if (overnightStartMinutes <= overnightEndMinutes) { // Same day
        isOvernight = midpointTimeOfDay >= overnightStartMinutes && midpointTimeOfDay < overnightEndMinutes;
      } else { // Crosses midnight
        isOvernight = midpointTimeOfDay >= overnightStartMinutes || midpointTimeOfDay < overnightEndMinutes;
      }
    }

    if (isOvernight) {
      switch(premiumType) {
        case 'offset_overtime': {
          const currentIndex = allMultipliers.findIndex(m => m.code === prevailingMultiplier.code);
          let nextMultiplier = allMultipliers[currentIndex + 1];
          if (!nextMultiplier) {
            nextMultiplier = prevailingMultiplier; // stay at top of ladder
          }
          const surchargePay = hoursInSegment * baseHourlyRate * nextMultiplier.defaultMultiplier!;
          premium = surchargePay - normalPay;
          effective_rate = `${nextMultiplier.name} (${nextMultiplier.defaultMultiplier!}x)`;
          break;
        }
        case 'hourly_premium': {
          const prevailingHourlyRate = baseHourlyRate * prevailingMultiplier.defaultMultiplier!;
          if (isOtInclusive) {
            if (premiumKind === 'percentage') {
              premium = (hoursInSegment * prevailingHourlyRate) * (premiumPercentage / 100);
              const premiumRate = prevailingHourlyRate * (premiumPercentage / 100);
              hourly_premium_rate = `$${premiumRate.toFixed(2)}/hr<br/>($${prevailingHourlyRate.toFixed(2)} × ${premiumPercentage}%)`;
              hourly_premium_rate_simple = `$${premiumRate.toFixed(2)}`;
            } else {
              premium = hoursInSegment * Number(premiumAmount);
              hourly_premium_rate = `$${Number(premiumAmount).toFixed(2)}/hr`;
              hourly_premium_rate_simple = `$${Number(premiumAmount).toFixed(2)}`;
            }
          } else { // Additive
            if (premiumKind === 'percentage') {
              premium = hoursInSegment * baseHourlyRate * (premiumPercentage / 100);
              const premiumRate = baseHourlyRate * (premiumPercentage / 100);
              hourly_premium_rate = `$${premiumRate.toFixed(2)}/hr<br/>($${baseHourlyRate.toFixed(2)} × ${premiumPercentage}%)`;
              hourly_premium_rate_simple = `$${premiumRate.toFixed(2)}`;
            } else {
              premium = hoursInSegment * Number(premiumAmount);
              hourly_premium_rate = `$${Number(premiumAmount).toFixed(2)}/hr`;
              hourly_premium_rate_simple = `$${Number(premiumAmount).toFixed(2)}`;
            }
          }
          break;
        }
        case 'credit_extra_time': {
          const creditPayRate = baseHourlyRate * prevailingMultiplier.defaultMultiplier!;
          premium = (creditMinutes / 60) * creditPayRate * hoursInSegment;
          break;
        }
      }
    }

    total_premium_pay += premium;

    const segmentStartTime = exampleShiftStartHour + segmentStart;
    const segmentEndTime = exampleShiftStartHour + segmentEnd;
    const formatSegmentTime = (h: number) => {
      const hour = Math.floor(h) % 24;
      const minute = Math.round((h - Math.floor(h)) * 60);
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
    }

    const hoursInSegmentStr = hoursInSegment.toFixed(2).replace(/\.00$/, '');
    const prevailingHourlyRateVal = baseHourlyRate * prevailingMultiplier.defaultMultiplier!;
    const normalPayStr = `$${normalPay.toFixed(2)}`;
    let premiumAssessedWithCalc = `$${premium.toFixed(2)}`;
    let creditValue = '—';
    let premiumAmountSimple = premium.toFixed(2);
    
    if (isOvernight && premium > 0) {
      if (premiumType === 'credit_extra_time') {
        const creditPayRate = baseHourlyRate * prevailingMultiplier.defaultMultiplier!;
        const creditValuePerHour = (creditMinutes / 60) * creditPayRate;
        creditValue = `${creditMinutes}m credit @ $${creditPayRate.toFixed(2)}/hr`;
        premiumAssessedWithCalc = `$${premium.toFixed(2)}<br/>($${creditValuePerHour.toFixed(2)} × ${hoursInSegmentStr}h)`;
        premiumAmountSimple = creditValuePerHour.toFixed(2);
      }
    } else if (!isOvernight) {
      premiumAssessedWithCalc = '—';
      premiumAmountSimple = '0.00';
    }

    // Calculate effective hourly rate for multiplier offset
    let effective_hourly_rate = prevailingHourlyRateVal;
    let multiplier_offset_premium_rate = '—';
    let multiplier_offset_premium_rate_simple = '—';
    
    if (isOvernight && premiumType === 'offset_overtime') {
      const currentIndex = allMultipliers.findIndex(m => m.code === prevailingMultiplier.code);
      let nextMultiplier = allMultipliers[currentIndex + 1];
      if (!nextMultiplier) {
        nextMultiplier = prevailingMultiplier;
      }
      effective_hourly_rate = baseHourlyRate * nextMultiplier.defaultMultiplier!;
      const premiumRate = effective_hourly_rate - prevailingHourlyRateVal;
      multiplier_offset_premium_rate = `$${premiumRate.toFixed(2)}/hr<br/>($${baseHourlyRate.toFixed(2)}/hr × ${nextMultiplier.defaultMultiplier!}x) - ($${baseHourlyRate.toFixed(2)}/hr × ${prevailingMultiplier.defaultMultiplier!}x)`;
      multiplier_offset_premium_rate_simple = `$${premiumRate.toFixed(2)}`;
    }

    // Format premium amount with calculation details
    let premiumAmountWithCalc = isOvernight && premium > 0 ? `$${premium.toFixed(2)}` : '—';
    if (isOvernight && premium > 0) {
      if (premiumType === 'offset_overtime') {
        premiumAmountWithCalc = `$${premium.toFixed(2)}`;
      }
    }

    rows.push({
      hours_worked: `${hoursInSegmentStr}h`,
      is_overnight: isOvernight ? '✅' : '❌',
      prevailing_rate: `${prevailingMultiplier.name} (${prevailingMultiplier.defaultMultiplier!}x)`,
      normal_pay: normalPayStr,
      premium_assessed: premiumAssessedWithCalc,
      final_pay: `$${(normalPay + premium).toFixed(2)}`,
      // for credit_extra_time
      credit_value: creditValue,
      premium_amount_simple: premiumAmountSimple,
      // for multiplier_offset
      effective_rate: effective_rate,
      premium: `$${premium.toFixed(2)}`,
      effective_hourly_rate: `$${effective_hourly_rate.toFixed(2)}`,
      multiplier_offset_premium_rate,
      multiplier_offset_premium_rate_simple,
      // for hourly_premium
      base_hourly_rate: `$${baseHourlyRate.toFixed(2)}/hr`,
      prevailing_hourly_rate: `$${prevailingHourlyRateVal.toFixed(2)}`,
      base_gross: `${normalPayStr}`,
      premium_amount: premiumAmountWithCalc,
      hourly_premium_rate,
      hourly_premium_rate_simple,
      time_period: `${formatSegmentTime(segmentStartTime)} - ${formatSegmentTime(segmentEndTime)}`,
      cumulative_weekly_hours: `${(midpoint + ((context as RateCalculationContext & { startingHoursInWeek?: number }).startingHoursInWeek || 0)).toFixed(2)}h`,
    });
  }

  // Calculate average hourly premium
  const total_hours_worked = rows.reduce((sum, row) => {
    const hours = parseFloat(String(row.hours_worked).replace('h', ''));
    return sum + hours;
  }, 0);
  
  const average_hourly_premium = total_hours_worked > 0 ? total_premium_pay / total_hours_worked : 0;
  
  return { rows, total_normal_pay, total_premium_pay, average_hourly_premium };
}

export function calculateOvernightMarkdownVariables(
  roles: Role[] = [],
  rateMultipliers: RateMultiplier[] = [],
  timeCreditMinutes: number = 30,
  minimumBillableHours: number | null = 10,
  premiumPercentageValue: number = 10,
  premiumFixedValue: number = 5,
  flatRateAmount: number = 50,
  startTime: string = '00:00',
  endTime: string = '06:00',
  isOtInclusive?: boolean,
  thresholdEnabled?: boolean,
  thresholdType?: 'percentage' | 'hours' | null,
  percentageThreshold?: number | null,
  hoursThreshold?: number | null,
  premiumType?: 'offset_overtime' | 'guaranteed_rate' | 'hourly_premium' | 'flat_rate' | 'credit_extra_time' | null,
  premiumKind?: 'percentage' | 'fixed' | null
) {

  const sampleRole = getSampleRole(roles);
  const isDayRate = sampleRole.billingStructure === 'day_rate';
  const effectiveMinimumBillableHours = minimumBillableHours || 10; // fallback to 10 if null
  const hourlyRateForCalc = isDayRate 
    ? (sampleRole.rate / effectiveMinimumBillableHours) 
    : sampleRole.rate;

  // Get the straight time multiplier
  const straightTimeMultiplierMatch = rateMultipliers.find(m => m.code === 'STRAIGHT_TIME');
  const straightTimeMultiplier = straightTimeMultiplierMatch?.defaultMultiplier ?? 1.0;
  const straightTimeMultiplierName = straightTimeMultiplierMatch?.name || 'Straight Time';
  
  // Use the canonical function to get the correct ladder for the sample role's billing structure.
  const sortedOvertimeMultipliers = getSortedOvertimeMultipliers(rateMultipliers, sampleRole.billingStructure);

  // Get the first overtime multiplier (or fallback)
  const firstOtMultiplier = sortedOvertimeMultipliers[0];
  const otMultiplier = firstOtMultiplier?.defaultMultiplier ?? 1.5;
  const otMultiplierName = firstOtMultiplier?.name || 'Overtime';

  // Get the second overtime multiplier (or fallback to first if only one exists)
  const secondOtMultiplier = sortedOvertimeMultipliers[1] || firstOtMultiplier;
  const dtMultiplier = secondOtMultiplier?.defaultMultiplier ?? 2.0;
  const dtMultiplierName = secondOtMultiplier?.name || 'Double Time';

  // Calculate example shift pay for flat rate scenario using billing structure config
  const exampleShiftHours = isDayRate ? effectiveMinimumBillableHours : 8;
  const baseGrossPay = isDayRate ? sampleRole.rate : (exampleShiftHours * hourlyRateForCalc);
  const finalGrossPayWithFlat = baseGrossPay + Number(flatRateAmount);

  // --- ADD NEW CALCULATIONS FOR MULTI-SHIFT EXAMPLE ---
  // Use the calculatePay utility to get a realistic base pay for a second shift example
  const calculationContext: RateCalculationContext & { startingHoursInWeek?: number } = { roles, rateMultipliers, minimumBillableHours: effectiveMinimumBillableHours };
  const baseGrossPay_shift2 = calculatePay(exampleShiftHours, calculationContext, 0).totalPay;
  const finalGrossPayWithFlat_shift2 = baseGrossPay_shift2 + Number(flatRateAmount);

  // Format time display (convert 24-hour to 12-hour format for readability)
  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const formattedStartTime = formatTime(startTime);
  const formattedEndTime = formatTime(endTime);

  // Generate example shift times based on the overnight window
  const generateShiftExamples = () => {
    // Parse start and end times
    const [startHour] = startTime.split(':').map(Number);
    const [endHour] = endTime.split(':').map(Number);
    
    // Create a shift that doesn't overlap (ends before start time)
    // For a 2:00 AM - 6:00 AM window, create a 4:00 PM - 12:00 AM shift
    const noOverlapEndHour = startHour === 0 ? 24 : startHour; // If start is midnight, end at midnight (24:00)
    let noOverlapStartHour = noOverlapEndHour - 8;
    if (noOverlapStartHour < 0) noOverlapStartHour += 24;
    
    // Handle the midnight case properly
    const noOverlapEndTime = noOverlapEndHour === 24 ? '12:00 AM' : formatTime(`${noOverlapEndHour.toString().padStart(2, '0')}:00`);
    const noOverlapStart = formatTime(`${noOverlapStartHour.toString().padStart(2, '0')}:00`);
    
    // Create a shift that overlaps (crosses the overnight window)
    // For a 2:00 AM - 6:00 AM window, create a 12:00 AM - 8:00 AM shift
    let overlapStartHour = startHour - 2;
    if (overlapStartHour < 0) overlapStartHour += 24;
    let overlapEndHour = endHour + 2;
    if (overlapEndHour >= 24) overlapEndHour -= 24;
    
    const overlapStart = formatTime(`${overlapStartHour.toString().padStart(2, '0')}:00`);
    const overlapEnd = formatTime(`${overlapEndHour.toString().padStart(2, '0')}:00`);
    
    // Calculate actual overlap duration
    const windowDuration = endHour >= startHour ? endHour - startHour : (24 - startHour) + endHour;
    const overlapDuration = `${windowDuration} hours`;
    
    return {
      noOverlapShiftExample: `${noOverlapStart} - ${noOverlapEndTime}`,
      overlapShiftExample: `${overlapStart} - ${overlapEnd}`,
      overlapDuration
    };
  };

  const shiftExamples = generateShiftExamples();

  // Build the full ladder for multiplier offset calculations
  const fullLadder = [straightTimeMultiplierMatch, ...sortedOvertimeMultipliers].filter(Boolean) as RateMultiplier[];
  const highestMultiplier = fullLadder[fullLadder.length - 1] || straightTimeMultiplierMatch;


  // Determine ladder type for display purposes
  let ladderType: 'day_rate' | 'hourly' = 'day_rate'; // Default
  if (sortedOvertimeMultipliers.length > 0) {
    const firstOt = sortedOvertimeMultipliers[0];
    if (sampleRole.billingStructure === 'hourly') {
      // For hourly, it's a daily ladder only if perShiftBillingStructures includes 'hourly'
      if (firstOt.defaultPerShiftThresholdHours != null && firstOt.perShiftBillingStructures?.includes('hourly')) {
        ladderType = 'day_rate';
      } else {
        ladderType = 'hourly';
      }
    } else { // day_rate role
      // For a day_rate role, getSortedOvertimeMultipliers prioritizes the daily ladder.
      // If the first returned OT multiplier has a daily threshold, it MUST be a daily ladder.
      if (firstOt.defaultPerShiftThresholdHours != null) {
        ladderType = 'day_rate';
      } else {
        ladderType = 'hourly'; // Otherwise it's the weekly ladder fallback.
      }
    }
  }

  // Generate rate multiplier ladder string
  const rateMultiplierLadder = getMultiplierListStringHorizontal(rateMultipliers, sampleRole.billingStructure, effectiveMinimumBillableHours);

  // --- Generate Example Pay Calculation ---
  let example_shift_duration: number;
  let startingHoursInWeek = 0;
  let example_scenario_context = '';

  if (ladderType === 'hourly') { // This indicates a weekly ladder is in use
    example_shift_duration = 12; // Use a standard shift length
    const firstWeeklyThreshold = getThreshold(sortedOvertimeMultipliers[0], 'hourly') || 40;
    // Assume we're starting the shift 8 hours before the first weekly threshold to demonstrate crossing it
    startingHoursInWeek = Math.max(0, firstWeeklyThreshold - 8);
    example_scenario_context = `(assuming ${startingHoursInWeek} hours worked previously in the week)`;
  } else { // Daily ladder
    const firstOtThreshold = getThreshold(sortedOvertimeMultipliers[0], ladderType);
    if (firstOtThreshold > 0) {
      // Make the shift long enough to cross the first OT threshold plus some extra, capped at 16h
      example_shift_duration = Math.min(firstOtThreshold + 4, 16);
    } else {
      example_shift_duration = 12; // Default duration if no OT thresholds
    }
  }

  calculationContext.startingHoursInWeek = startingHoursInWeek;

  const [startHour] = startTime.split(':').map(Number);
  const example_shift_start_hour = (startHour - 2 + 24) % 24; // Start the shift 2 hours before the premium window
  const exampleShiftEndHour = example_shift_start_hour + example_shift_duration;
  const formattedExampleStart = formatTime(`${example_shift_start_hour.toString().padStart(2, '0')}:00`);
  
  // Handle decimal hours properly for end time
  const endHour = Math.floor(exampleShiftEndHour) % 24;
  const endMinutes = Math.round((exampleShiftEndHour - Math.floor(exampleShiftEndHour)) * 60);
  const formattedExampleEnd = formatTime(`${endHour.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`);
  const example_scenario_title = `A ${example_shift_duration.toFixed(2).replace(/\.00$/, '')}-hour shift from ${formattedExampleStart} to ${formattedExampleEnd}`;

  const overnightStartMinutes = parseTime(startTime);
  const overnightEndMinutes = parseTime(endTime);

  const { 
    rows: example_pay_calculation_rows, 
    total_normal_pay, 
    total_premium_pay,
    average_hourly_premium
  } = generateOvernightExamplePayCalculationRows(example_shift_duration, calculationContext, {
    premiumType: premiumType ?? null,
    startTime,
    endTime,
    exampleShiftStartHour: example_shift_start_hour,
    // Pass all possible options
    creditMinutes: timeCreditMinutes,
    premiumKind: premiumKind,
    premiumPercentage: premiumPercentageValue,
    premiumAmount: premiumFixedValue,
    isOtInclusive: isOtInclusive,
  });

  // Threshold calculation
  let example_overlap_hours = 0;
  if (overnightStartMinutes !== null && overnightEndMinutes !== null) {
      for (let hour = 0; hour < example_shift_duration; hour++) {
          const midpointTimeOfDay = ((example_shift_start_hour * 60) + ((hour + 0.5) * 60)) % (24 * 60);
          if (overnightStartMinutes <= overnightEndMinutes) { // Same day
              if (midpointTimeOfDay >= overnightStartMinutes && midpointTimeOfDay < overnightEndMinutes) {
                  example_overlap_hours += 1;
              }
          } else { // Crosses midnight
              if (midpointTimeOfDay >= overnightStartMinutes || midpointTimeOfDay < overnightEndMinutes) {
                  example_overlap_hours += 1;
              }
          }
      }
  }

  const example_overlap_percentage = (example_overlap_hours / example_shift_duration) * 100;
  let threshold_is_met = true; // Default to true if not enabled
  let threshold_check_summary = '';

  if (thresholdEnabled) {
      if (thresholdType === 'hours') {
          const required = hoursThreshold ?? 0;
          threshold_is_met = example_overlap_hours >= required;
          threshold_check_summary = `Threshold ${threshold_is_met ? 'met' : 'not met'}: ${example_overlap_hours.toFixed(2).replace(/\.00$/, '')} overlap hours is ${threshold_is_met ? '≥' : '<'} ${required} required hours.`;
      } else if (thresholdType === 'percentage') {
          const required = percentageThreshold ?? 0;
          threshold_is_met = example_overlap_percentage >= required;
          threshold_check_summary = `Threshold ${threshold_is_met ? 'met' : 'not met'}: ${example_overlap_percentage.toFixed(2)}% overlap is ${threshold_is_met ? '≥' : '<'} ${required}% required.`;
      }
  }


  return {
    timeCreditMinutes,
    getSampleRole: sampleRole,
    sampleRoleBillingStructure: sampleRole.billingStructure,
    minimumBillableHours: effectiveMinimumBillableHours,
    Math,

    overnight_hourly_premium_percentage: Number(premiumPercentageValue) || 10,
    overnight_hourly_premium_amount: (Number(premiumFixedValue) || 5).toFixed(2),
    flatRateAmount: (Number(flatRateAmount) || 50).toFixed(2),
    highest_rate_name: highestMultiplier?.name || 'Straight Time',

    // Dynamic values for flat rate example
    baseGrossPay: baseGrossPay.toFixed(2),
    finalGrossPayWithFlat: finalGrossPayWithFlat.toFixed(2),
    // --- ADD NEW VARIABLES ---
    baseGrossPay_shift2: baseGrossPay_shift2.toFixed(2),
    finalGrossPayWithFlat_shift2: finalGrossPayWithFlat_shift2.toFixed(2),

    // Time window variables
    startTime: formattedStartTime,
    endTime: formattedEndTime,
    overnight_premium_basis: isOtInclusive ? 'ot_inclusive' : 'base_rate',
    thresholdEnabled: thresholdEnabled,
    thresholdType: thresholdType,
    percentageThreshold,
    hoursThreshold,
    overnight_hourly_premium_type: premiumKind,
    ...shiftExamples,
    // New example table data
    example_pay_calculation_rows,
    total_normal_pay: total_normal_pay.toFixed(2),
    total_premium_pay: total_premium_pay.toFixed(2),
    total_final_pay: (total_normal_pay + total_premium_pay).toFixed(2),
    example_overlap_hours: example_overlap_hours.toFixed(2).replace(/\.00$/, ''),
    example_overlap_percentage: example_overlap_percentage.toFixed(2),
    sampleRoleUnit: isDayRate ? 'day' : 'hr',
    proratedRateDisplay: isDayRate ? ` ($${hourlyRateForCalc.toFixed(2)}/hr prorated)` : '',
    threshold_is_met,
    threshold_check_summary,
    rateMultiplierLadder,
    example_scenario_title,
    example_scenario_context,
    ladderType,
    example_role_rate: sampleRole.rate.toFixed(2),
    average_hourly_premium: average_hourly_premium?.toFixed(2) || '0.00',
    isPremiumTypeFixed: premiumKind === 'fixed',
  };
}
